import { ErrorState, LoadingState, StaleBanner } from '../../components/ui/AsyncState';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoveryQuery as useQuery } from '../../components/ui/useRecoveryQuery';
import {
  getNotificationPolicies,
  createNotificationPolicyDraft,
  activateNotificationPolicy,
} from '../../api/endpoints/admin';
import { useMutationHook } from '../../hooks/useMutationHook';
import { parseAndValidateRecipientPhones } from '../../utils/phone';
import styles from './SettingsView.module.css';
import { staticQueryPolicy } from '../../api/queryPolicies';
import { Button } from '../../components/ui/Button';

export function NotificationPoliciesTab() {
  const { t } = useTranslation();
  const { data: policies, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'notification-policies'],
    queryFn: () => getNotificationPolicies(),
    ...staticQueryPolicy,
  });

  const [isDrafting, setIsDrafting] = useState(false);
  const activePolicy = policies?.find((p) => p.status === 'ACTIVE') ?? policies?.[0];

  const [waveSize, setWaveSize] = useState('10');
  const [waveInterval, setWaveInterval] = useState('10');
  const [maxWaves, setMaxWaves] = useState('4');
  const [minBids, setMinBids] = useState('2');
  const [stopWhenMinimumBidsReached, setStopWhenMinimumBidsReached] = useState(true);
  const [maxVendors, setMaxVendors] = useState('40');
  const [cooldown, setCooldown] = useState('30');
  const [fcmFallbackEnabled, setFcmFallbackEnabled] = useState(true);
  const [excludedShipperPrefixes, setExcludedShipperPrefixes] = useState('');
  const [allowAllRecipients, setAllowAllRecipients] = useState(true);
  const [allowedRecipientPhones, setAllowedRecipientPhones] = useState('');
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutationHook(
    async () => {
      const parsedPrefixes = excludedShipperPrefixes
        .split(/[\n,;]+/)
        .map((s) => s.trim().replace(/\s+/g, ''))
        .filter(Boolean);
      const { validPhones } = parseAndValidateRecipientPhones(allowedRecipientPhones);

      const draft = await createNotificationPolicyDraft({
        name: activePolicy?.name || 'DEFAULT_FTL_NOTIFICATION',
        configuration: {
          waves: {
            size: Number(waveSize),
            intervalMinutes: Number(waveInterval),
            maxWaves: Number(maxWaves),
          },
          bidding: {
            minimumBids: Number(minBids),
            stopWhenMinimumBidsReached,
          },
          limits: {
            maximumVendors: Number(maxVendors),
            vendorCooldownMinutes: Number(cooldown),
          },
          channels: {
            primary: 'WHATSAPP',
            fallback: fcmFallbackEnabled ? 'FCM' : null,
          },
          testFilters: {
            excludedShipperPhonePrefixes: parsedPrefixes,
            allowAllRecipients,
            allowedRecipientPhones: allowAllRecipients ? [] : validPhones,
          },
        },
      });
      return await activateNotificationPolicy(draft.id);
    },
    {
      successMessage: 'New notification policy version created and activated successfully.',
      invalidateKeys: [['admin', 'notification-policies'], ['admin', 'notification']],
      onSuccess: () => {
        setIsDrafting(false);
        setError(null);
      },
    },
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (Number(waveSize) < 1 || Number(waveSize) > 50) {
      setError('Wave size must be between 1 and 50.');
      return;
    }
    if (Number(waveInterval) < 1) {
      setError('Wave interval must be at least 1 minute.');
      return;
    }
    if (Number(maxWaves) < 1) {
      setError('Maximum waves must be at least 1.');
      return;
    }
    if (Number(minBids) < 1) {
      setError('Minimum bids target must be at least 1.');
      return;
    }
    if (Number(maxVendors) < Number(waveSize)) {
      setError('Maximum partners must be greater than or equal to wave size.');
      return;
    }
    if (Number(cooldown) < 0) {
      setError('Anti-fatigue cooldown cannot be negative.');
      return;
    }
    if (!allowAllRecipients) {
      const { validPhones, invalidEntries } = parseAndValidateRecipientPhones(allowedRecipientPhones);
      if (invalidEntries.length > 0) {
        setError(`Invalid phone number(s): "${invalidEntries.join('", "')}". Recipient numbers must be 10 digits.`);
        return;
      }
      if (validPhones.length === 0) {
        setError('At least one valid 10-digit recipient phone number is required when delivery is restricted.');
        return;
      }
    }
    setError(null);
    saveMut.mutate(undefined);
  };

  if (isLoading) return <LoadingState label={t('settings.notificationPolicies.loading')} />;
  if (isError && !policies) return <ErrorState title={t('settings.notificationPolicies.loadError')} onRetry={() => refetch()} />;

  return (
    <div>
      {isError && <StaleBanner onRetry={() => refetch()} />}
      {/* Active Policy Card */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div>
            <h3 className={styles.cardTitle}>
              {activePolicy?.name ?? 'Default FTL Notification Policy'}
            </h3>
            <p className={styles.cardHint}>
              {t('settings.notificationPolicies.activeHint')}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className={styles.versionBadge}>{t('settings.notificationPolicies.versionLabel', { version: activePolicy?.version ?? 1 })}</span>
            {!isDrafting && (
              <Button
                type="button"
                variant="secondary" size="sm"
                onClick={() => {
                  if (activePolicy) {
                    const config = activePolicy.configuration;
                    setWaveSize(String(config.waves.size));
                    setWaveInterval(String(config.waves.intervalMinutes));
                    setMaxWaves(String(config.waves.maxWaves));
                    setMinBids(String(config.bidding.minimumBids));
                    setStopWhenMinimumBidsReached(config.bidding.stopWhenMinimumBidsReached);
                    setMaxVendors(String(config.limits.maximumVendors));
                    setCooldown(String(config.limits.vendorCooldownMinutes));
                    setFcmFallbackEnabled(config.channels.fallback === 'FCM');
                    const filters = config.testFilters;
                    setExcludedShipperPrefixes(filters?.excludedShipperPhonePrefixes?.join(', ') ?? '');
                    setAllowAllRecipients(filters?.allowAllRecipients ?? true);
                    setAllowedRecipientPhones(filters?.allowedRecipientPhones?.join('\n') ?? '');
                  }
                  setIsDrafting(true);
                }}
              >
                {t('settings.notificationPolicies.newVersion')}
              </Button>
            )}
          </div>
        </div>

        {/* Parameters Grid */}
        <div className={styles.paramsGrid}>
          <div className={styles.paramCard}>
            <div className={styles.paramLabel}>{t('settings.notificationPolicies.waveSizeTitle')}</div>
            <div className={styles.paramVal}>{t('settings.notificationPolicies.valueVendors', { value: activePolicy?.configuration.waves.size ?? 10 })}</div>
          </div>
          <div className={styles.paramCard}>
            <div className={styles.paramLabel}>{t('settings.notificationPolicies.waveIntervalTitle')}</div>
            <div className={styles.paramVal}>{t('settings.notificationPolicies.valueMin', { value: activePolicy?.configuration.waves.intervalMinutes ?? 10 })}</div>
          </div>
          <div className={styles.paramCard}>
            <div className={styles.paramLabel}>{t('settings.notificationPolicies.earlyHaltTitle')}</div>
            <div className={styles.paramVal}>{t('settings.notificationPolicies.valueBids', { value: activePolicy?.configuration.bidding.minimumBids ?? 2 })}</div>
          </div>
          <div className={styles.paramCard}>
            <div className={styles.paramLabel}>{t('settings.notificationPolicies.maxVendorsTitle')}</div>
            <div className={styles.paramVal}>{activePolicy?.configuration.limits.maximumVendors ?? 40}</div>
          </div>
          <div className={styles.paramCard}>
            <div className={styles.paramLabel}>{t('settings.notificationPolicies.cooldownTitle')}</div>
            <div className={styles.paramVal}>{t('settings.notificationPolicies.valueMin', { value: activePolicy?.configuration.limits.vendorCooldownMinutes ?? 30 })}</div>
          </div>
          <div className={styles.paramCard}>
            <div className={styles.paramLabel}>{t('settings.notificationPolicies.channelsTitle')}</div>
            <div className={styles.paramVal} style={{ fontSize: 13 }}>
              {t('settings.notificationPolicies.channelsPrimary')}
              {activePolicy?.configuration.channels.fallback === 'FCM' ? ' → FCM fallback' : ''}
            </div>
          </div>
          <div className={styles.paramCard}>
            <div className={styles.paramLabel}>{t('settings.notificationPolicies.excludedPrefixesTitle')}</div>
            <div className={styles.paramVal} style={{ fontSize: 13, wordBreak: 'break-all' }}>
              {activePolicy?.configuration.testFilters?.excludedShipperPhonePrefixes?.length
                ? activePolicy.configuration.testFilters.excludedShipperPhonePrefixes.join(', ')
                : 'None'}
            </div>
          </div>
          <div className={styles.paramCard}>
            <div className={styles.paramLabel}>{t('settings.notificationPolicies.recipientDeliveryTitle')}</div>
            <div className={styles.paramVal} style={{ fontSize: 13 }}>
              {activePolicy?.configuration.testFilters?.allowAllRecipients !== false ? (
                <span style={{ color: 'var(--status-green-text)', fontWeight: 600 }}>{t('settings.notificationPolicies.allRecipients')}</span>
              ) : (
                <span style={{ color: 'var(--status-amber-text)', fontWeight: 600 }}>
                  {t('settings.notificationPolicies.restrictedRecipients', {
                    allowed: activePolicy.configuration.testFilters.allowedRecipientPhones?.length ?? 0,
                  })}
                </span>
              )}
            </div>
          </div>
        </div>

        {activePolicy?.updatedAt && (
          <div className={styles.meta}>
            {t('settings.notificationPolicies.activeVersionUpdated', {
              version: activePolicy.version,
              date: new Date(activePolicy.updatedAt).toLocaleString('en-IN'),
            })}
          </div>
        )}
      </div>

      {/* Drafting Form */}
      {isDrafting && (
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>
            {t('settings.notificationPolicies.draftTitle', { version: (activePolicy?.version ?? 0) + 1 })}
          </h3>
          <p className={styles.cardHint}>
            {t('settings.notificationPolicies.draftHint')}
          </p>

          <form onSubmit={handleSubmit}>
            <div className={styles.formGrid}>
              <div>
                <label className={styles.label} htmlFor="input-waveSize">
                  {t('settings.notificationPolicies.waveSizeLabel')}
                </label>
                <div className={styles.inputWrap}>
                  <input
                    id="input-waveSize"
                    className={styles.input}
                    type="number"
                    min="1"
                    max="50"
                    value={waveSize}
                    onChange={(e) => setWaveSize(e.target.value)}
                  />
                  <span className={styles.pctSuffix}>{t('settings.notificationPolicies.unitVendors')}</span>
                </div>
              </div>

              <div>
                <label className={styles.label} htmlFor="input-waveInterval">
                  {t('settings.notificationPolicies.waveIntervalLabel')}
                </label>
                <div className={styles.inputWrap}>
                  <input
                    id="input-waveInterval"
                    className={styles.input}
                    type="number"
                    min="1"
                    max="120"
                    value={waveInterval}
                    onChange={(e) => setWaveInterval(e.target.value)}
                  />
                  <span className={styles.pctSuffix}>{t('settings.notificationPolicies.unitMin')}</span>
                </div>
              </div>

              <div>
                <label className={styles.label} htmlFor="input-maxWaves">
                  {t('settings.notificationPolicies.maxWavesLabel')}
                </label>
                <div className={styles.inputWrap}>
                  <input
                    id="input-maxWaves"
                    className={styles.input}
                    type="number"
                    min="1"
                    max="50"
                    value={maxWaves}
                    onChange={(e) => setMaxWaves(e.target.value)}
                  />
                  <span className={styles.pctSuffix}>{t('settings.notificationPolicies.unitWaves')}</span>
                </div>
              </div>

              <div>
                <label className={styles.label} htmlFor="input-minBids">
                  {t('settings.notificationPolicies.minBidsLabel')}
                </label>
                <div className={styles.inputWrap}>
                  <input
                    id="input-minBids"
                    className={styles.input}
                    type="number"
                    min="1"
                    max="20"
                    value={minBids}
                    onChange={(e) => setMinBids(e.target.value)}
                  />
                  <span className={styles.pctSuffix}>{t('settings.notificationPolicies.unitBids')}</span>
                </div>
              </div>

              <div>
                <label className={styles.label} htmlFor="input-maxVendors">
                  {t('settings.notificationPolicies.maxVendorsLabel')}
                </label>
                <div className={styles.inputWrap}>
                  <input
                    id="input-maxVendors"
                    className={styles.input}
                    type="number"
                    min="1"
                    max="500"
                    value={maxVendors}
                    onChange={(e) => setMaxVendors(e.target.value)}
                  />
                  <span className={styles.pctSuffix}>{t('settings.notificationPolicies.unitVendors')}</span>
                </div>
              </div>

              <div>
                <label className={styles.label} htmlFor="input-cooldown">
                  {t('settings.notificationPolicies.cooldownLabel')}
                </label>
                <div className={styles.inputWrap}>
                  <input
                    id="input-cooldown"
                    className={styles.input}
                    type="number"
                    min="5"
                    max="1440"
                    value={cooldown}
                    onChange={(e) => setCooldown(e.target.value)}
                  />
                  <span className={styles.pctSuffix}>{t('settings.notificationPolicies.unitMin')}</span>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
                <span className={styles.label}>{t('settings.notificationPolicies.dispatchRouting')}</span>
                <span style={{ fontSize: 13 }}>{t('settings.notificationPolicies.primaryWhatsApp')}</span>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={fcmFallbackEnabled}
                    onChange={(e) => setFcmFallbackEnabled(e.target.checked)}
                  />
                  <span>{t('settings.notificationPolicies.fcmFallback')}</span>
                </label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={stopWhenMinimumBidsReached}
                    onChange={(e) => setStopWhenMinimumBidsReached(e.target.checked)}
                  />
                  <span>{t('settings.notificationPolicies.stopWhenMinBids')}</span>
                </label>
              </div>

              <div style={{ gridColumn: 'span 2', marginTop: 8, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
                <h4 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
                  {t('settings.notificationPolicies.testFilteringTitle')}
                </h4>
                <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--muted)' }}>
                  {t('settings.notificationPolicies.testFilteringHint')}
                </p>
              </div>

              <div style={{ gridColumn: 'span 2' }}>
                <label className={styles.label} htmlFor="input-excludedPrefixes">
                  {t('settings.notificationPolicies.excludedPrefixesLabel')}
                </label>
                <div className={styles.inputWrap}>
                  <input
                    id="input-excludedPrefixes"
                    className={styles.input}
                    type="text"
                    placeholder={t('settings.notificationPolicies.excludedPrefixesPlaceholder')}
                    value={excludedShipperPrefixes}
                    onChange={(e) => setExcludedShipperPrefixes(e.target.value)}
                  />
                </div>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                  {t('settings.notificationPolicies.excludedPrefixesHint')}
                </span>
              </div>

              <div style={{ gridColumn: 'span 2' }}>
                <span className={styles.label}>{t('settings.notificationPolicies.deliveryModeLabel')}</span>
                <div style={{ display: 'flex', gap: 16, marginTop: 6, marginBottom: 8 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="deliveryMode"
                      checked={allowAllRecipients}
                      onChange={() => setAllowAllRecipients(true)}
                    />
                    <span>{t('settings.notificationPolicies.allowAllVendors')}</span>
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="deliveryMode"
                      checked={!allowAllRecipients}
                      onChange={() => setAllowAllRecipients(false)}
                    />
                    <span>{t('settings.notificationPolicies.restrictToTest')}</span>
                  </label>
                </div>
              </div>

              {!allowAllRecipients && (
                <div style={{ gridColumn: 'span 2' }}>
                  <label className={styles.label} htmlFor="input-allowedPhones">
                    {t('settings.notificationPolicies.allowedPhonesLabel')}
                  </label>
                  <textarea
                    id="input-allowedPhones"
                    className={styles.input}
                    style={{ minHeight: 70, resize: 'vertical', fontFamily: 'var(--font-mono, monospace)' }}
                    placeholder="8522803275&#10;9876543210"
                    value={allowedRecipientPhones}
                    onChange={(e) => setAllowedRecipientPhones(e.target.value)}
                  />
                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                    {t('settings.notificationPolicies.allowedPhonesHint')}
                  </span>
                </div>
              )}
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.actions}>
              <Button
                type="button"
                variant="secondary" size="sm"
                onClick={() => {
                  setIsDrafting(false);
                  setError(null);
                }}
              >
                {t('common.actions.cancel')}
              </Button>
              <button type="submit" className={styles.submit} disabled={saveMut.isLoading}>
                {saveMut.isLoading ? 'Activating…' : 'Save & Activate Version'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Revision History */}
      {policies && policies.length > 0 && (
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>{t('settings.notificationPolicies.historyTitle')}</h3>
          <div className={styles.historyTableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('settings.notificationPolicies.colVersion')}</th>
                  <th>{t('settings.notificationPolicies.colStatus')}</th>
                  <th>{t('settings.notificationPolicies.colWaveSize')}</th>
                  <th>{t('settings.notificationPolicies.colInterval')}</th>
                  <th>{t('settings.notificationPolicies.colMinBids')}</th>
                  <th>{t('settings.notificationPolicies.colCooldown')}</th>
                  <th>{t('settings.notificationPolicies.colUpdatedAt')}</th>
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{t('settings.notificationPolicies.versionLabel', { version: p.version })}</td>
                    <td>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: p.status === 'ACTIVE' ? 'var(--status-green-text)' : 'var(--muted)',
                        }}
                      >
                        {p.status}
                      </span>
                    </td>
                    <td>{t('settings.notificationPolicies.valueVendors', { value: p.configuration.waves.size })}</td>
                    <td>{t('settings.notificationPolicies.valueMin', { value: p.configuration.waves.intervalMinutes })}</td>
                    <td>{t('settings.notificationPolicies.valueBids', { value: p.configuration.bidding.minimumBids })}</td>
                    <td>{t('settings.notificationPolicies.valueMin', { value: p.configuration.limits.vendorCooldownMinutes })}</td>
                    <td>
                      {p.updatedAt ? new Date(p.updatedAt).toLocaleString('en-IN') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
